import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import GlobalWebSocketService from '../services/globalsocket';
import { handleDiscussionWs, DISCUSSION_WS_EVENT } from '../helpers/api/discussions/discussion-ws-handler';
import { handleUnifiedNotification, NOTIFICATION_WS_EVENT } from '../helpers/api/notifications/notification-handler';
import {
  NOTIFICATION_RECENT_HOURS,
  NOTIFICATION_RECENT_LIST_PARAMS,
  notificationKeys,
  notificationService,
} from '../helpers/api/notifications/notifications';
import { messageI18n, tToast } from '../helpers/i18n/toastI18n';
import { useTranslation } from 'react-i18next';

interface GlobalState {
  isConnected: boolean;
  connectionError: string | null;
  lastMessage: Record<string, unknown> | null;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  newInstanceNotification: any | null;
}

interface GlobalContextValue extends GlobalState {
  getState: () => GlobalState;
  connect: () => void;
  disconnect: () => void;
  sendMessage: (message: Record<string, unknown>) => void;
  resetError: () => void;
  clearNewInstanceNotification: () => void;
}

const DEFAULT_STATE: GlobalState = {
  isConnected: false,
  connectionError: null,
  lastMessage: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 3,
  newInstanceNotification: null,
};

export const GlobalContext = createContext<GlobalContextValue | undefined>(
  undefined
);

type GlobalAction =
  | { type: 'SET_CONNECTED'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MESSAGE'; payload: Record<string, unknown> }
  | { type: 'INCREMENT_RECONNECT_ATTEMPTS' }
  | { type: 'RESET_RECONNECT_ATTEMPTS' }
  | { type: 'RESET_ERROR' }
  | { type: 'SET_NEW_INSTANCE_NOTIFICATION'; payload: any }
  | { type: 'CLEAR_NEW_INSTANCE_NOTIFICATION' };

const reducer = (state: GlobalState, action: GlobalAction): GlobalState => {
  switch (action.type) {
    case 'SET_CONNECTED':
      return {
        ...state,
        isConnected: action.payload,
        connectionError: action.payload ? null : state.connectionError,
        reconnectAttempts: action.payload ? 0 : state.reconnectAttempts,
      };
    case 'SET_ERROR':
      return { ...state, connectionError: action.payload, isConnected: false };
    case 'SET_MESSAGE':
      return { ...state, lastMessage: action.payload };
    case 'INCREMENT_RECONNECT_ATTEMPTS':
      return { ...state, reconnectAttempts: state.reconnectAttempts + 1 };
    case 'RESET_RECONNECT_ATTEMPTS':
      return { ...state, reconnectAttempts: 0 };
    case 'RESET_ERROR':
      return { ...state, connectionError: null };
    case 'SET_NEW_INSTANCE_NOTIFICATION':
      return { ...state, newInstanceNotification: action.payload };
    case 'CLEAR_NEW_INSTANCE_NOTIFICATION':
      return { ...state, newInstanceNotification: null };
    default:
      return state;
  }
};

export default function GlobalContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);
  const queryClient = useQueryClient();
  const wsRef = useRef<GlobalWebSocketService | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { t } = useTranslation('Toast');

  const getWebSocketUrl = useCallback(() => {
    const API =
      (window as any).RUNTIME_ENV?.BACKEND_URL ??
      process.env.BACKEND_URL ??
      'http://localhost:8000/api/v1';

    const { protocol, host } = new URL(API);

    return `${protocol.replace('http', 'ws')}//${host}/ws/global`;
  }, []);

  const getState = useCallback(() => state, [state]);

  const resetError = useCallback(() => {
    dispatch({ type: 'RESET_ERROR' });
    if (wsRef.current) {
      wsRef.current.resetErrorState();
    }
  }, []);

  const clearNewInstanceNotification = useCallback(() => {
    dispatch({ type: 'CLEAR_NEW_INSTANCE_NOTIFICATION' });
  }, []);

  const handleWebSocketMessage = useCallback((event: MessageEvent) => {
    let messageData: Record<string, unknown>;
    try {
      messageData = JSON.parse(event.data) as Record<string, unknown>;
    } catch {
      dispatch({
        type: 'SET_ERROR',
        payload: tToast('common.webSocketParseFailed'),
      });
      messageI18n.error('common.webSocketParseFailed');
      return;
    }

    try {
      dispatch({ type: 'SET_MESSAGE', payload: messageData });

      if (
        messageData.event === DISCUSSION_WS_EVENT ||
        messageData.event === 'clinical_discussion'
      ) {
        handleDiscussionWs(messageData, queryClient);
        return;
      }

      if (
        messageData.event === NOTIFICATION_WS_EVENT ||
        messageData.type === NOTIFICATION_WS_EVENT
      ) {
        handleUnifiedNotification(messageData, {
          queryClient,
          onNewInstance: (payload) => {
            dispatch({ type: 'SET_NEW_INSTANCE_NOTIFICATION', payload });
          },
        });
        // Collaboration notifications embed discussion metadata — refresh threads too.
        handleDiscussionWs(messageData, queryClient);
        return;
      }

      switch (messageData.type) {
        case 'error':
          dispatch({ type: 'SET_ERROR', payload: messageData.message as string });
          message.error(messageData.message as string)|| (tToast('common.webSocketError'));
          break;
        case 'status_update':
          break;
        default:
          handleUnifiedNotification(messageData, {
            queryClient,
            onNewInstance: (payload) => {
              dispatch({ type: 'SET_NEW_INSTANCE_NOTIFICATION', payload });
            },
          });
          break;
      }
    } catch (error) {
      console.error('WebSocket message handler failed:', error, messageData);
    }
  }, [queryClient]);

  const handleWebSocketError = useCallback(
    (error: string) => {
      console.error('Global WebSocket Error:', error);
      dispatch({ type: 'SET_ERROR', payload: error });

      if (!state.connectionError) {
        message.error(error || tToast('common.webSocketConnectionError'));
      }

      if (state.reconnectAttempts < state.maxReconnectAttempts) {
        dispatch({ type: 'INCREMENT_RECONNECT_ATTEMPTS' });


        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        reconnectTimeoutRef.current = setTimeout(
          () => {
            connect();
          },
          2000 * (state.reconnectAttempts + 1)
        );
      } else {
        console.warn(
          '❌ Max reconnect attempts reached. Connection failed permanently.'
        );
        message.error(tToast('common.webSocketMaxReconnect'));
      }
    },
    [state.reconnectAttempts, state.maxReconnectAttempts, state.connectionError]
  );

  const connect = useCallback(() => {
    if (wsRef.current?.isConnected()) {
      return;
    }

    if (wsRef.current) {
      wsRef.current.disconnect();
    }

    try {
      const wsUrl = getWebSocketUrl();

      wsRef.current = new GlobalWebSocketService(wsUrl);

      // Set up callbacks
      wsRef.current.setOnErrorCallback(handleWebSocketError);
      wsRef.current.setOnMessageCallback(handleWebSocketMessage);

      wsRef.current.setOnOpenCallback(() => {
        dispatch({ type: 'SET_CONNECTED', payload: true });
        dispatch({ type: 'RESET_RECONNECT_ATTEMPTS' });
        resetError();
      });

      wsRef.current.setOnCloseCallback((event: CloseEvent) => {
        dispatch({ type: 'SET_CONNECTED', payload: false });

        if (event.code === 1011) {
          handleWebSocketError('Backend crashed unexpectedly');
        } else if (event.code !== 1000) {
          handleWebSocketError('WebSocket connection closed unexpectedly');
        }
      });

      wsRef.current.connect();
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      handleWebSocketError('Failed to create WebSocket connection');
    }
  }, [
    getWebSocketUrl,
    handleWebSocketError,
    handleWebSocketMessage,
    resetError,
  ]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.disconnect();
      wsRef.current = null;
    }

    dispatch({ type: 'SET_CONNECTED', payload: false });
    dispatch({ type: 'RESET_RECONNECT_ATTEMPTS' });
  }, []);

  const sendMessage = useCallback((messageData: unknown) => {
    if (wsRef.current?.isConnected()) {
      wsRef.current.sendMessage(messageData);
    } else {
      console.warn('WebSocket is not connected. Cannot send message.');
      dispatch({ type: 'SET_ERROR', payload: 'WebSocket is not connected' });
    }
  }, []);

  useEffect(() => {
    void queryClient.prefetchQuery({
      queryKey: notificationKeys.list(NOTIFICATION_RECENT_LIST_PARAMS),
      queryFn: () => notificationService.list(NOTIFICATION_RECENT_LIST_PARAMS),
      staleTime: 30_000,
    });
    void queryClient.prefetchQuery({
      queryKey: notificationKeys.unread(NOTIFICATION_RECENT_HOURS),
      queryFn: () => notificationService.unreadCount(NOTIFICATION_RECENT_HOURS),
      staleTime: 30_000,
    });
  }, [queryClient]);

  useEffect(() => {
    connect();

    const handleBeforeUnload = () => {
      if (wsRef.current?.isConnected()) {
        wsRef.current.disconnect();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      disconnect();
    };
  }, [connect, disconnect]);

  useEffect(() => {
    if (
      !state.isConnected &&
      state.reconnectAttempts === 0 &&
      !state.connectionError
    ) {
      connect();
    }
  }, [
    connect,
    state.isConnected,
    state.reconnectAttempts,
    state.connectionError,
  ]);

  const value: GlobalContextValue = {
    ...state,
    getState,
    connect,
    disconnect,
    sendMessage,
    resetError,
    clearNewInstanceNotification,
  };

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
}

GlobalContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useGlobalWebSocket = (): GlobalContextValue => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error(
      'useGlobalWebSocket must be used within a GlobalContextProvider'
    );
  }
  return context;
};
